"use client";

import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexReactClient } from "convex/react";
import { ReactNode } from "react";

const convex = new ConvexReactClient(
  import.meta.env.NEXT_PUBLIC_CONVEX_URL as string
);
let hasLoggedAuthWarning = false;
const clerkAppearance = {
  elements: {
    footer: "hidden",
    footerAction: "hidden",
  },
} as const;

function getValidClerkPublishableKey(rawKey: string | undefined): string | null {
  const normalized = rawKey?.trim();
  if (
    !normalized ||
    normalized === "-" ||
    normalized.toLowerCase() === "undefined" ||
    normalized.toLowerCase() === "null"
  ) {
    return null;
  }

  if (
    !normalized.startsWith("pk_test_") &&
    !normalized.startsWith("pk_live_")
  ) {
    return null;
  }

  return normalized;
}

function decodeClerkFrontendApi(key: string) {
  const parts = key.split("_");
  if (parts.length !== 3) return null;
  try {
    const decoded = atob(parts[2]);
    if (!decoded.endsWith("$")) return null;
    return decoded.slice(0, -1).toLowerCase();
  } catch {
    return null;
  }
}

function isLocalHost(hostname: string) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
}

function isPreviewHost(hostname: string) {
  return hostname.endsWith(".pages.dev") || hostname.endsWith(".workers.dev");
}

function hostMatchesFrontendApi(hostname: string, frontendApi: string) {
  const candidates = new Set<string>([frontendApi]);
  if (frontendApi.startsWith("clerk.")) {
    candidates.add(frontendApi.slice("clerk.".length));
  }

  return Array.from(candidates).some(
    (candidate) => hostname === candidate || hostname.endsWith(`.${candidate}`)
  );
}

function resolveClerkPublishableKey() {
  const primary = getValidClerkPublishableKey(
    import.meta.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY as string | undefined
  );
  const preview = getValidClerkPublishableKey(
    import.meta.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PREVIEW as string | undefined
  );
  const dev = getValidClerkPublishableKey(
    import.meta.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_DEV as string | undefined
  );

  if (typeof window === "undefined") {
    const fallback = primary ?? preview ?? dev;
    return {
      key: fallback,
      reason: fallback
        ? null
        : "The Clerk publishable key is missing or invalid. Set NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY.",
    };
  }

  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host)) {
    const localKey = primary ?? dev;
    return localKey
      ? { key: localKey, reason: null as string | null }
      : {
          key: null as string | null,
          reason:
            "Authentication is disabled on localhost because no Clerk key is configured. Set NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY or NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_DEV.",
        };
  }

  if (isPreviewHost(host) && preview) {
    return { key: preview, reason: null as string | null };
  }

  if (!primary) {
    return {
      key: null as string | null,
      reason:
        "The Clerk publishable key is missing or invalid. Set NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY.",
    };
  }

  if (primary.startsWith("pk_test_")) {
    return {
      key: null as string | null,
      reason:
        "Authentication is disabled on this host because it is using a Clerk test key. Set NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY to a pk_live_* key for non-local environments.",
    };
  }

  if (primary.startsWith("pk_live_")) {
    const frontendApi = decodeClerkFrontendApi(primary);
    if (frontendApi && !hostMatchesFrontendApi(host, frontendApi)) {
      return {
        key: null as string | null,
        reason: `Authentication is disabled on this host (${host}). This production Clerk key is restricted to ${frontendApi}.`,
      };
    }
  }

  return { key: primary, reason: null as string | null };
}

export function ConvexClientProvider({ children }: { children: ReactNode }) {
  const { key: clerkPublishableKey, reason: invalidReason } = resolveClerkPublishableKey();

  if (!clerkPublishableKey) {
    if (import.meta.env.DEV && !hasLoggedAuthWarning) {
      hasLoggedAuthWarning = true;
      console.warn(
        invalidReason ??
          "Invalid NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY. Expected a key starting with pk_test_ or pk_live_."
      );
    }

    return (
      <div className="mx-auto flex min-h-screen w-full max-w-xl items-center justify-center p-6 text-center">
        <div className="space-y-2 rounded-xl border border-border bg-card p-6">
          <h1 className="text-lg font-semibold">Authentication unavailable</h1>
          <p className="text-sm text-muted-foreground">
            {invalidReason ? (
              invalidReason
            ) : (
              <>
                The Clerk publishable key is missing or invalid. Set{" "}
                <code>NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY</code> to a valid{" "}
                <code>pk_test_*</code> or <code>pk_live_*</code> value.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      appearance={clerkAppearance}
      signInUrl="/login"
      signUpUrl="/signup"
      afterSignOutUrl="/"
    >
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        {children}
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}
